<script setup lang="ts">
// FOF home page (spec 2026-10-12 §4, redesigned 2026-11-02 — spec docs/superpowers/specs/2026-11-02-fof-home-
// redesign-blue-box-design.md §4): one card per fund the viewer can see. The whole card / row is the link to the
// fund's page. The API (/api/fof/overview) already filters gated funds. Polls every 60 s while mounted and visible.
// Paolo 2026-11-03: live funds above the dark ones (a "Dark funds" heading between), rows grouped by futures family
// (equity index, metals, energy); BUY rows above SELL rows inside each section (fofFormat).
// Paolo 2026-11-03 18:1x — the TWO-CHART ROW ("Two-Chart FOF Row" brainstorm): the overnight and anchored charts become
// ONE futures chart, labelled with the future's symbol in its colour (the symbol leaves the fund name); the K_t chart
// stays. Each chart is one path coloured by section between the bars — the previous exchange close (grey, "ON ›"),
// the 08:40 decision (in the ON section's colour) and the 12:00 strike (blue) — each section by its own drift
// (SectionSpark / sectionSpark.ts). The futures headline is the move since the strike with the ON move beside it;
// the K_t headline is the move against the latest official NAV, its revaluation drawn as a step on the strike bar.
// Hover (desktop) or tap (phone) zooms a chart (SectionSparkZoom).
// Paolo 2026-11-03 19:xx: an "(i)" next to Future and K_t (table head and the cards' legend line) opens FofChartLegend —
// the full-size drawing of one live row plus a key and English text with that row's numbers (the % is measured from
// the valuation point = the blue line; the K_t revaluation against the official NAV is the step on the blue bar).
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { api, type FofOverview, type FofFundRow, type FofSeries } from '../../api/client'
import { useAuth } from '../../composables/useAuth'
import { statusColour, fofHealthInline } from '../signalHealthHelpers'
import SectionSparkZoom from '../SectionSparkZoom.vue'
import FofChartLegend, { type LegendExample } from './FofChartLegend.vue'
import { futureColor } from '../edge/futurePalette'
import { onPct, strikeStepIndex } from '../sectionSpark'
import { UP_GOOD, DOWN_BAD } from '../sparkline'
import { signedPct, moveClass, signalDateNote, rowHref, todayLondonIso, decisionPillClass, livePillText, positionLine, orderFofRows, fofRowBreaks, futureFamily } from './fofFormat'

const POLL_MS = 60_000
const data = ref<FofOverview | null>(null)
const failed = ref(false)
let timer: ReturnType<typeof setInterval> | null = null
// FOF is the default landing page: a logged-out visitor gets a way in, never a 401 error.
const { isAuthenticated, goToLogin } = useAuth()

async function load() {
  if (!isAuthenticated.value) return
  if (typeof document !== 'undefined' && document.hidden) return
  try {
    data.value = await api.fofOverview()
    failed.value = false
  } catch {
    failed.value = true
  }
}

onMounted(() => { void load(); timer = setInterval(load, POLL_MS) })
onUnmounted(() => { if (timer) clearInterval(timer); timer = null })
watch(isAuthenticated, (now) => { if (now) void load() })

const today = computed(() => todayLondonIso())
const host = typeof window !== 'undefined' ? window.location.hostname : ''

interface Cell {
  key: 'future' | 'kt'; lbl: string; colour: string | null; series: number[]; value: string; cls: 'up' | 'down' | 'flat'; sub: string
  closeAt: number | null; decisionAt: number | null; markAt: number | null; baseValue: number | null; baseLabel: string; markLabel: string
  stepFrom: number | null; stepTo: number | null; startUtc: string | null; endUtc: string | null; unit: string; dp: number
  decisionColour: string | null   // the futures chart's ON colour; the K_t cell borrows it (Paolo 2026-11-03 19:3x)
}
/** The futures chart's decision-bar colour: the ON section's — green when the decision value is at or above the close
 * value, red below — null when either value is missing (the chart then falls back to its own rule). */
function onColour(s: FofSeries | null): string | null {
  const on = onPct(s?.closeValue, s?.decisionValue)
  return on == null ? null : on >= 0 ? UP_GOOD : DOWN_BAD
}
function cell(key: Cell['key'], lbl: string, colour: string | null, s: FofSeries | null, baseLabel: string, markLabel: string, unit: string, dp: number,
  decisionColour: string | null = null): Cell {
  const on = key === 'future' ? onPct(s?.closeValue, s?.decisionValue) : null
  return {
    key, lbl, colour, series: s?.series ?? [], value: signedPct(s?.pct ?? null), cls: moveClass(s?.pct ?? null),
    sub: on == null ? '' : `ON ${signedPct(on)}`, decisionColour,
    closeAt: s?.closeAt ?? null, decisionAt: s?.decisionAt ?? null, markAt: s?.markAt ?? null,
    baseValue: s?.markValue ?? null, baseLabel, markLabel,
    stepFrom: s?.stepFrom ?? null, stepTo: s?.stepTo ?? null,
    startUtc: s?.startUtc ?? null, endUtc: s?.endUtc ?? null, unit, dp,
  }
}

const rows = computed(() => {
  const ordered = orderFofRows(data.value?.funds ?? [])
  const breaks = fofRowBreaks(ordered)
  return ordered.map((r: FofFundRow, i) => {
  const note = signalDateNote(r, today.value)
  const strike = `${r.anchorTimeUk} strike`
  return {
    r,
    heading: breaks[i].heading,
    familyStart: breaks[i].familyStart,
    family: futureFamily(r.futuresSymbol),
    href: rowHref(r.slug, host),
    cells: [
      cell('future', r.futuresSymbol ?? '—', futureColor(r.futuresSymbol), r.anchored, 'strike', strike, '', 2),
      cell('kt', 'K_t', null, r.kt, 'official NAV', strike, 'p', 2, onColour(r.anchored)),
    ],
    decision: r.decision ?? '—',
    decisionCls: decisionPillClass(r.decision),
    decisionTitle: note ? `Signal of ${note}` : '',
    decisionNote: note,
    live: livePillText(r.live),
    position: positionLine(r),
    healthDot: statusColour(r.health),
    healthText: fofHealthInline(r),
  }
  })
})

function go(href: string) { window.location.assign(href) }

/** The row the legend explains with: the first whose chart has a path and has reached today's strike (for K_t, one
 * with a revaluation step when any row has one), else the first with a path, else null. */
function legendExample(key: Cell['key']): LegendExample | null {
  const all = rows.value.map(x => ({ x, c: x.cells.find(c => c.key === key)! })).filter(p => p.c.series.length >= 2)
  const pick = (key === 'kt' ? all.find(p => strikeStepIndex(p.c.series, p.c.markAt, p.c.stepFrom, p.c.stepTo) >= 0) : undefined)
    ?? all.find(p => p.c.markAt != null) ?? all[0]
  if (!pick) return null
  const { x, c } = pick
  const s = key === 'future' ? x.r.anchored : x.r.kt
  return {
    fundLabel: x.r.label, symbol: c.lbl, strike: c.markLabel, pctText: c.value, values: c.series,
    closeAt: c.closeAt, decisionAt: c.decisionAt, markAt: c.markAt, closeValue: s?.closeValue ?? null, decisionValue: s?.decisionValue ?? null,
    markValue: c.baseValue, stepFrom: c.stepFrom, stepTo: c.stepTo, startUtc: c.startUtc, endUtc: c.endUtc, unit: c.unit, dp: c.dp,
    decisionColour: c.decisionColour,
  }
}
const legendFuture = computed(() => legendExample('future'))
const legendKt = computed(() => legendExample('kt'))
</script>

<template>
  <section class="fof-home" data-testid="fof-home">
    <p v-if="!isAuthenticated" class="fof-login">
      <button type="button" data-testid="fof-login" @click="goToLogin()">Log in to see your funds</button>
    </p>
    <p v-else-if="failed && !data" class="fof-error">Overview unavailable</p>
    <template v-else>
      <!-- ≥ 640 px: the seven-column table (two-chart row, Paolo 2026-11-03). -->
      <table class="fof-table">
        <thead>
          <tr><th>Fund</th><th>Future<FofChartLegend kind="future" :example="legendFuture" /></th><th>K_t<FofChartLegend kind="kt" :example="legendKt" /></th><th>Signal</th><th>Live</th><th>Status</th><th>Position</th></tr>
        </thead>
        <tbody>
          <template v-for="x in rows" :key="x.r.fund">
          <tr v-if="x.heading" class="fof-heading" data-testid="fof-heading"><td colspan="7">{{ x.heading }}</td></tr>
          <tr :data-fund="x.r.fund" :data-family="x.family" class="fof-row" :class="{ 'family-start': x.familyStart }" @click="go(x.href)">
            <td class="fof-name"><a :href="x.href" @click.stop>{{ x.r.label }}</a></td>
            <td v-for="c in x.cells" :key="c.key" class="fof-chart" :data-cell="c.key">
              <SectionSparkZoom :values="c.series" :label="`${x.r.label} · ${c.lbl}`" :value-text="c.value" :sub-text="c.sub"
                                :close-at="c.closeAt" :decision-at="c.decisionAt" :mark-at="c.markAt" :base-value="c.baseValue" :base-label="c.baseLabel" :mark-label="c.markLabel"
                                :step-from="c.stepFrom" :step-to="c.stepTo" :start-utc="c.startUtc" :end-utc="c.endUtc" :unit="c.unit" :dp="c.dp" :decision-colour="c.decisionColour" />
              <div class="cap"><span class="name sym" :style="c.colour ? { color: c.colour } : undefined" :title="c.key === 'future' ? x.family : ''">{{ c.lbl }}</span><span class="v move" :class="c.cls">{{ c.value }}</span><span v-if="c.sub" class="sub">{{ c.sub }}</span></div>
            </td>
            <td class="fof-signal"><span class="pill" :class="x.decisionCls" :title="x.decisionTitle" data-testid="fof-decision">{{ x.decision }}</span><span v-if="x.decisionNote" class="note">{{ x.decisionNote }}</span></td>
            <td class="fof-live"><span v-if="x.live" class="pill" :class="x.live.toLowerCase()" data-testid="fof-live">{{ x.live }}</span></td>
            <td class="fof-health" :title="x.r.healthReason ?? ''" data-testid="fof-health"><span class="dot" :style="{ background: x.healthDot }"></span>{{ x.healthText }}</td>
            <td class="fof-position" :class="x.position.cls">{{ x.position.text }}</td>
          </tr>
          </template>
        </tbody>
      </table>

      <!-- < 640 px: the cards (spec §4.1), two charts each. -->
      <div class="fof-cards">
        <div class="legend"><span>future since the strike · ON = since the previous close<FofChartLegend kind="future" :example="legendFuture" /></span><span>K_t = synth NAV vs the official NAV<FofChartLegend kind="kt" :example="legendKt" /></span></div>
        <template v-for="x in rows" :key="x.r.fund">
        <div v-if="x.heading" class="fof-heading" data-testid="fof-heading">{{ x.heading }}</div>
        <a class="fof-card" :class="{ 'family-start': x.familyStart }" data-testid="fof-card" :href="x.href" :data-fund="x.r.fund" :data-family="x.family">
          <div class="l1">
            <span class="name">{{ x.r.label }}</span>
            <span class="sp"></span>
            <span class="pill" :class="x.decisionCls" :title="x.decisionTitle" data-testid="fof-decision">{{ x.decision }}</span>
            <span v-if="x.live" class="pill" :class="x.live.toLowerCase()" data-testid="fof-live">{{ x.live }}</span>
            <span class="health" :title="x.r.healthReason ?? ''" data-testid="fof-health"><span class="dot" :style="{ background: x.healthDot }"></span>{{ x.healthText }}</span>
          </div>
          <div class="l2">
            <div v-for="c in x.cells" :key="c.key" class="cell" :data-cell="c.key">
              <div class="cap"><span class="lbl name sym" :style="c.colour ? { color: c.colour } : undefined">{{ c.lbl }}</span><span class="v move" :class="c.cls">{{ c.value }}</span><span v-if="c.sub" class="sub">{{ c.sub }}</span></div>
              <SectionSparkZoom :values="c.series" :label="`${x.r.label} · ${c.lbl}`" :value-text="c.value" :sub-text="c.sub"
                                :close-at="c.closeAt" :decision-at="c.decisionAt" :mark-at="c.markAt" :base-value="c.baseValue" :base-label="c.baseLabel" :mark-label="c.markLabel"
                                :step-from="c.stepFrom" :step-to="c.stepTo" :start-utc="c.startUtc" :end-utc="c.endUtc" :unit="c.unit" :dp="c.dp" :decision-colour="c.decisionColour" />
            </div>
          </div>
          <div class="l3">
            <span class="pos" :class="x.position.cls">{{ x.position.text }}</span>
            <span class="anchor">strike {{ x.r.anchorTimeUk }}</span>
          </div>
        </a>
        </template>
      </div>
    </template>
  </section>
</template>

<style scoped>
.fof-home { display: flex; flex-direction: column; gap: 0.5rem; }
.fof-error { color: #ef5350; }
.fof-login button { padding: 8px 14px; background: #1f3a5a; color: #e0e0e0; border: 0; border-radius: 4px; cursor: pointer; }

.move.up { color: #4caf50; }
.move.down { color: #ef5350; }
.move.flat { color: #9e9e9e; }
.sym { font-family: 'JetBrains Mono', Consolas, monospace; font-size: 11px; font-weight: 700; }   /* colour = the future's (futurePalette); K_t stays neutral */
.fof-heading { color: #9e9e9e; font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; }
.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
.pill { display: inline-block; padding: 1px 7px; border-radius: 9px; font-size: 10px; font-weight: 700; letter-spacing: .3px; line-height: 15px; }
.pill.buy { background: #1f4a2f; color: #4caf50; }
.pill.sell { background: #5a1f1f; color: #ef5350; }
.pill.other, .pill.none { background: #2a2a2a; color: #9e9e9e; }
.pill.live { background: #1f3a5a; color: #6ea8df; }
.pill.dark { background: #2a2a2a; color: #9e9e9e; border: 1px solid #444; }
.up { color: #4caf50; }
.down { color: #ef5350; }
.cap { display: flex; align-items: baseline; gap: 8px; font-family: 'JetBrains Mono', Consolas, monospace; font-size: 12px; font-variant-numeric: tabular-nums; }
.cap .sub { color: #9e9e9e; font-size: 11px; }

/* table (≥ 640 px) */
.fof-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.fof-table th, .fof-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #2a2a2a; white-space: nowrap; vertical-align: middle; }
.fof-table th { color: #9e9e9e; font-weight: 500; font-size: 11px; }
.fof-row { cursor: pointer; }
.fof-row:hover td { background: #1c1c1c; }
.fof-row.family-start td { border-top: 1px solid #3a3a3a; }   /* a futures-family boundary */
.fof-table tr.fof-heading td { padding: 10px 8px 4px; border-bottom: 0; }
.fof-name a { color: inherit; font-weight: 700; text-decoration: none; }
.fof-name a:hover { text-decoration: underline; }
.fof-chart { width: 30%; min-width: 180px; }
.fof-chart :deep(svg.section-spark.mini) { width: 100%; height: 36px; }
.fof-chart .cap { margin-top: 2px; }
.fof-signal .note { color: #9e9e9e; font-size: 0.8em; margin-left: 6px; }

/* cards (< 640 px) — mockup fof-layout.html option A */
.fof-cards { display: none; flex-direction: column; gap: 6px; }
.legend { font-size: 10px; color: #777; padding: 2px 6px 4px; display: flex; gap: 12px; flex-wrap: wrap; }
.fof-card { display: block; text-decoration: none; color: inherit; background: #1a1a1a; border-radius: 6px; padding: 8px 10px; }
.fof-card:hover { background: #222; }
.fof-card.family-start:not(:first-of-type) { margin-top: 6px; }   /* a futures-family boundary */
.fof-cards .fof-heading { padding: 8px 6px 0; }
.l1 { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.l1 .name { font-weight: 700; font-size: 14px; }
.l1 .sp { flex: 1; }
.l1 .health { font-size: 11px; color: #bdbdbd; }
/* both mini charts in ONE row (Paolo 2026-11-03: "much better to have both graphs in one row") — mockup card_row_mock.html */
.l2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 6px; }
.l2 .cell { min-width: 0; display: grid; gap: 2px; }
.l2 .cell :deep(svg.section-spark.mini) { height: 40px; }
.l2 .cap { gap: 6px; font-size: 11px; flex-wrap: wrap; }
.l2 .cap .sub { font-size: 10px; }
.lbl { font-size: 11px; color: #9e9e9e; letter-spacing: .4px; }
.l3 { display: flex; justify-content: space-between; gap: 8px; margin-top: 6px; font-size: 11px; color: #bdbdbd; }
.l3 .anchor { color: #9e9e9e; }
@media (max-width: 639px) {
  .fof-table { display: none; }
  .fof-cards { display: flex; }
}
</style>
